/* =========================================================================
 * SampleData — shows researchers a realistic JSON sample of the two data
 * shapes they'll work with:
 *
 *   1. Location epoch summary (Firestore doc at
 *      devices/{uid}/location/{hash}). Records 20 location samples + per-
 *      sensor rollups for the same time window.
 *   2. Sensor batch (Cloud Storage JSON at data/{uid}/{sensor}/{hash}.json).
 *      The full readings array for one sensor in one epoch.
 *
 * Field names and types match the live schema as produced by the Android
 * app's FirestoreUploader. Values here are mocked: a plausible 20-second
 * walking session in Buenos Aires.
 * ========================================================================= */

import { useState } from "react";

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import useMediaQuery from "@mui/material/useMediaQuery";

import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

// ---------------------------------------------------------------------------
// Sample payloads
// ---------------------------------------------------------------------------

const LOCATION_EPOCH = {
  areaId: "ba_a3f9",
  cycle: 1780162917379,
  deviceAddress: "anon_a3f9b7e2c1d4",
  hash: "0f8069730aa9042951c73820e0015b184081e6fed73f960aefda908f6995b5b0",
  signature: "",
  uploadTimestamp: 1780163100,
  firstTimestamp: 1780163080,
  lastTimestamp: 1780163100,
  rows: 20,
  firstLat: -34.6037,
  firstLng: -58.3816,
  lastLat: -34.6041,
  lastLng: -58.3824,
  minAlt: 25.4,
  maxAlt: 26.1,
  minSpeed: 0.0,
  maxSpeed: 1.8,
  mode: "walking",
  storagePath:
    "data/anon_a3f9b7e2c1d4/location/0f8069730aa9042951c73820e0015b184081e6fed73f960aefda908f6995b5b0.json",
  pathSamples: [
    { lat: -34.6037, lng: -58.3816, t: 1780163080 },
    { lat: -34.6038, lng: -58.3818, t: 1780163086 },
    { lat: -34.6039, lng: -58.382, t: 1780163092 },
    { lat: -34.604, lng: -58.3822, t: 1780163096 },
    { lat: -34.6041, lng: -58.3824, t: 1780163100 },
  ],
  barometer: { rows: 20, files: 1, hashes: ["b7c4ed12..."] },
  accelerometer: { rows: 20, files: 1, hashes: ["d12a89e4..."] },
  gyroscope: { rows: 20, files: 1, hashes: ["a4b5c6d7..."] },
  magnetometer: { rows: 20, files: 1, hashes: ["e8f2a991..."] },
  compass: { rows: 20, files: 1, hashes: ["c45d8bb0..."] },
  light: { rows: 20, files: 1, hashes: ["a91b2233..."] },
  microphone: { rows: 20, files: 1, hashes: ["f6a3df12..."] },
};

// ---- Sensor batch builder -------------------------------------------------
// Every per-sensor batch shares the same wrapper. Only the `unit` and the
// `readings[]` shape vary. Hashes here are mocked but distinct.

const BASE = {
  deviceAddress: "anon_a3f9b7e2c1d4",
  cycle: 1780162917379,
  rows: 20,
  firstTimestamp: 1780163080,
  lastTimestamp: 1780163100,
  uploadTimestamp: 1780163100,
};

const batch = (sensorType, unit, hash, readings) => ({
  sensorType,
  ...BASE,
  hash,
  rows: readings.length,
  unit,
  readings,
});

// 20 timestamps spanning the epoch window
const TIMES = Array.from({ length: 20 }, (_, i) => 1780163080 + i);

// Helpers that produce realistic-feeling jittered series
const floatSeries = (start, step, jitter) =>
  TIMES.map((t, i) => ({
    value: Math.round((start + step * i + Math.sin(i) * jitter) * 100) / 100,
    t,
  }));
const xyzSeries = (cx, cy, cz, jitter) =>
  TIMES.map((t, i) => ({
    x: Math.round((cx + Math.sin(i / 2) * jitter) * 1000) / 1000,
    y: Math.round((cy + Math.cos(i / 2) * jitter) * 1000) / 1000,
    z: Math.round((cz + Math.sin(i / 3) * jitter) * 1000) / 1000,
    t,
  }));

const BAROMETER_BATCH = batch(
  "barometer",
  "hPa",
  "b7c4ed12fa90df4287edbb2c34c1f099a013d27e5ca40b9d23b3318c6a45a812",
  floatSeries(1013.25, -0.005, 0.01)
);

const ACCELEROMETER_BATCH = batch(
  "accelerometer",
  "m/s²",
  "a12c8f4d6b9e037c4f3aa1d29eb78c1f2b4d6e8090123456789abcdef0123456",
  xyzSeries(0.12, -9.78, 0.34, 0.4)
);

const GYROSCOPE_BATCH = batch(
  "gyroscope",
  "rad/s",
  "9f3a7b1c5d2e84a6c0f81b3d5e7f9a2c4b6d8e0f1a3b5c7d9e1f3a5b7c9d1e3f",
  xyzSeries(0.02, -0.01, 0.005, 0.03)
);

const MAGNETOMETER_BATCH = batch(
  "magnetometer",
  "μT",
  "3c5e7f9a1b3d5e7f9a1c3d5e7f9b1c3d5e7f9a1b3c5d7e9f1a3b5c7d9e1f3a5b",
  xyzSeries(22.4, -3.1, -41.6, 1.2)
);

const DEVICE_MOTION_BATCH = batch(
  "deviceMotion",
  "degrees (α/β/γ)",
  "7d9f1a3b5c7e9f1a3b5d7f9a1c3e5f7a9b1c3d5e7f9a1b3c5d7e9f1a3b5c7d9e",
  TIMES.map((t, i) => ({
    x: Math.round((184.2 + Math.sin(i / 4) * 3) * 100) / 100,
    y: Math.round((12.5 + Math.cos(i / 4) * 1.5) * 100) / 100,
    z: Math.round((-3.8 + Math.sin(i / 5) * 1) * 100) / 100,
    t,
  }))
);

const COMPASS_BATCH = batch(
  "compass",
  "degrees from magnetic north",
  "1b3d5f7a9c1e3f5a7c9e1b3d5f7a9c1e3f5a7c9e1b3d5f7a9c1e3f5a7c9e1b3d",
  TIMES.map((t, i) => ({
    value: Math.round(((142.7 + i * 0.3) % 360) * 10) / 10,
    t,
  }))
);

const LIGHT_BATCH = batch(
  "light",
  "lux",
  "5a7c9e1b3d5f7a9c1e3f5a7c9e1b3d5f7a9c1e3f5a7c9e1b3d5f7a9c1e3f5a7c",
  floatSeries(8200, 12, 80)
);

const MICROPHONE_BATCH = batch(
  "microphone",
  "amplitude (0.0–1.0)",
  "f6a3df12c4e7891b0a3d5f7c9e1b3d5f7a9c1e3f5a7c9e1b3d5f7a9c1e3f5a7c",
  TIMES.map((t, i) => ({
    value: Math.round((0.15 + Math.abs(Math.sin(i / 2)) * 0.2) * 100) / 100,
    t,
  }))
);

const CARRIER_BATCH = batch(
  "carrier",
  "mixed (name / type / dBm)",
  "c1d3e5f7a9b1c3d5e7f9a1b3c5d7e9f1a3b5c7d9e1f3a5b7c9d1e3f5a7b9c1d3",
  TIMES.map((t, i) => ({
    carrierName: "Movistar",
    networkType: "LTE",
    signalStrengthDbm: -78 + (i % 4 === 0 ? -2 : 1),
    t,
  }))
);

// Each tab is either the location-epoch summary (electric blue) or a per-
// sensor batch (teal). Two color identities keep the type distinction visible
// while researchers cycle through the per-sensor payloads.
const TEAL = "#14B8A6";
const SAMPLES = [
  {
    key: "epoch",
    label: "Location epoch summary",
    sub: "summary",
    data: LOCATION_EPOCH,
    color: "#2563FF",
  },
  { key: "barometer", label: "Barometer", sub: "hPa", data: BAROMETER_BATCH, color: TEAL },
  {
    key: "accelerometer",
    label: "Accelerometer",
    sub: "m/s² · xyz",
    data: ACCELEROMETER_BATCH,
    color: TEAL,
  },
  { key: "gyroscope", label: "Gyroscope", sub: "rad/s · xyz", data: GYROSCOPE_BATCH, color: TEAL },
  {
    key: "magnetometer",
    label: "Magnetometer",
    sub: "μT · xyz",
    data: MAGNETOMETER_BATCH,
    color: TEAL,
  },
  {
    key: "deviceMotion",
    label: "Device motion",
    sub: "α/β/γ degrees",
    data: DEVICE_MOTION_BATCH,
    color: TEAL,
  },
  { key: "compass", label: "Compass", sub: "heading degrees", data: COMPASS_BATCH, color: TEAL },
  { key: "light", label: "Light", sub: "lux", data: LIGHT_BATCH, color: TEAL },
  {
    key: "microphone",
    label: "Microphone",
    sub: "amplitude 0–1",
    data: MICROPHONE_BATCH,
    color: TEAL,
  },
  { key: "carrier", label: "Carrier", sub: "network info", data: CARRIER_BATCH, color: TEAL },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SampleData() {
  const [tab, setTab] = useState(0);
  // Remembers which sensor batch the user last picked. Lets the mobile
  // "Sensor batch" tab restore the prior selection when they toggle back from
  // the Location tab. Defaults to Barometer (index 1).
  const [lastSensorIdx, setLastSensorIdx] = useState(1);
  const [copied, setCopied] = useState(false);
  // CSS breakpoint match — true under the `md` threshold (≈900px).
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const current = SAMPLES[tab];
  const json = JSON.stringify(current.data, null, 2);

  // Mobile tab axis: 0 = Location, 1 = Sensor batch (whichever sensor is
  // currently active counts as "batch"). Desktop ignores this.
  const mobileTab = tab === 0 ? 0 : 1;
  const onMobileTabChange = (_, v) => {
    if (v === 0) setTab(0);
    else setTab(lastSensorIdx);
  };
  const onSensorSelect = (e) => {
    const idx = Number(e.target.value);
    setTab(idx);
    setLastSensorIdx(idx);
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — silent */
    }
  };

  return (
    <MKBox
      component="section"
      py={{ xs: 8, md: 12 }}
      px={{ xs: 2, md: 4 }}
      sx={({ palette }) => ({ borderTop: `1px solid ${palette.grey[200]}` })}
    >
      <Container>
        <Grid container justifyContent="center" textAlign="center" mb={{ xs: 4, md: 6 }}>
          <Grid item xs={12} md={8}>
            <MKTypography
              variant="overline"
              color="primary"
              fontWeight="bold"
              sx={{ letterSpacing: "0.1em" }}
            >
              Sample data
            </MKTypography>
            <MKTypography
              variant="h2"
              color="dark"
              mt={1}
              sx={{ fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.15 }}
            >
              What an observation actually looks like
            </MKTypography>
            <MKTypography variant="body1" color="text" mt={2} sx={{ maxWidth: 640, mx: "auto" }}>
              The schema below matches what the contributor app uploads — a 20-second epoch from a
              walking session in Buenos Aires. Field names and types are real; values are mocked.
            </MKTypography>
          </Grid>
        </Grid>

        <MKBox
          sx={({ palette, borders }) => ({
            borderRadius: borders.borderRadius.lg,
            border: `1px solid ${palette.grey[200]}`,
            overflow: "hidden",
            boxShadow: "0 12px 32px -16px rgba(15, 23, 42, 0.18)",
          })}
        >
          {/* Tab bar — mobile collapses 12 tabs into 2 (Location / Sensor batch)
              with a Select below to pick which sensor. Desktop shows the full
              scrollable strip. */}
          <MKBox
            sx={({ palette }) => ({
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: `1px solid ${palette.grey[200]}`,
              background: palette.grey[100],
              pr: 1,
            })}
          >
            {isMobile ? (
              <Tabs
                value={mobileTab}
                onChange={onMobileTabChange}
                variant="fullWidth"
                sx={{ minHeight: 48, flex: 1, minWidth: 0 }}
                TabIndicatorProps={{
                  sx: {
                    height: 3,
                    backgroundColor: mobileTab === 0 ? SAMPLES[0].color : TEAL,
                    transition: "all 0.25s ease",
                  },
                }}
              >
                <Tab
                  label={
                    <MKTypography
                      variant="caption"
                      fontWeight="bold"
                      sx={({ palette }) => ({
                        textTransform: "none",
                        color: mobileTab === 0 ? SAMPLES[0].color : palette.dark.main,
                      })}
                    >
                      Location epoch
                    </MKTypography>
                  }
                  sx={{ textTransform: "none", py: 1.25, minHeight: 48 }}
                />
                <Tab
                  label={
                    <MKTypography
                      variant="caption"
                      fontWeight="bold"
                      sx={({ palette }) => ({
                        textTransform: "none",
                        color: mobileTab === 1 ? TEAL : palette.dark.main,
                      })}
                    >
                      Sensor batch
                    </MKTypography>
                  }
                  sx={{ textTransform: "none", py: 1.25, minHeight: 48 }}
                />
              </Tabs>
            ) : (
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{ minHeight: 48, flex: 1, minWidth: 0 }}
                TabIndicatorProps={{
                  sx: {
                    height: 3,
                    backgroundColor: current.color,
                    transition: "all 0.25s ease",
                  },
                }}
              >
                {SAMPLES.map((s, i) => {
                  const isActive = i === tab;
                  return (
                    <Tab
                      key={s.key}
                      label={
                        <MKBox
                          textAlign="left"
                          sx={{
                            position: "relative",
                            pl: 1.25,
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              left: 0,
                              top: 2,
                              bottom: 2,
                              width: 3,
                              borderRadius: 2,
                              background: s.color,
                              opacity: isActive ? 1 : 0.35,
                              transition: "opacity 0.2s ease",
                            },
                          }}
                        >
                          <MKTypography
                            variant="caption"
                            fontWeight="bold"
                            sx={({ palette }) => ({
                              textTransform: "none",
                              display: "block",
                              lineHeight: 1.2,
                              color: isActive ? s.color : palette.dark.main,
                              transition: "color 0.2s ease",
                            })}
                          >
                            {s.label}
                          </MKTypography>
                          <MKTypography
                            variant="caption"
                            sx={({ palette }) => ({
                              color: palette.grey[600],
                              textTransform: "none",
                              fontFamily: "monospace",
                              fontSize: 11,
                              display: "block",
                              lineHeight: 1.2,
                              mt: 0.25,
                            })}
                          >
                            {s.sub}
                          </MKTypography>
                        </MKBox>
                      }
                      sx={{
                        textTransform: "none",
                        py: 1.25,
                        minHeight: 48,
                        "&.Mui-selected": { color: "inherit" },
                      }}
                    />
                  );
                })}
              </Tabs>
            )}
            <Tooltip title={copied ? "Copied" : "Copy JSON"}>
              <IconButton onClick={onCopy} size="small" sx={{ mr: 1 }}>
                <Icon fontSize="small">{copied ? "check" : "content_copy"}</Icon>
              </IconButton>
            </Tooltip>
          </MKBox>

          {/* Mobile-only sensor picker — only visible while on the "Sensor
              batch" mobile tab. Lets researchers cycle through all 11 sensor
              batches without the giant tab strip. */}
          {isMobile && mobileTab === 1 && (
            <MKBox
              sx={({ palette }) => ({
                px: 2,
                py: 1.5,
                borderBottom: `1px solid ${palette.grey[200]}`,
                background: palette.grey[100],
              })}
            >
              <FormControl size="small" fullWidth>
                <Select
                  value={tab}
                  onChange={onSensorSelect}
                  sx={({ palette }) => ({
                    background: palette.white.main,
                    "& .MuiSelect-select": { py: 1, fontSize: 14 },
                  })}
                >
                  {SAMPLES.filter((s) => s.key !== "epoch").map((s) => {
                    const idx = SAMPLES.indexOf(s);
                    return (
                      <MenuItem key={s.key} value={idx} sx={{ fontSize: 14 }}>
                        {s.label}
                        <MKBox
                          component="span"
                          sx={({ palette }) => ({
                            color: palette.grey[600],
                            fontFamily: "monospace",
                            fontSize: 12,
                            ml: 1,
                          })}
                        >
                          · {s.sub}
                        </MKBox>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </MKBox>
          )}

          {/* JSON viewer */}
          <MKBox
            sx={{
              maxHeight: { xs: 420, md: 540 },
              overflow: "auto",
              "& pre": {
                margin: "0 !important",
                padding: "20px !important",
                fontSize: "13px !important",
                background: "#0F172A !important",
                lineHeight: "1.55 !important",
              },
            }}
          >
            <SyntaxHighlighter
              language="json"
              style={oneDark}
              showLineNumbers
              wrapLongLines={false}
            >
              {json}
            </SyntaxHighlighter>
          </MKBox>
        </MKBox>

        <MKTypography
          variant="caption"
          color="text"
          sx={{ display: "block", textAlign: "center", mt: 3, opacity: 0.75 }}
        >
          Delivered datasets are aggregated across thousands of these epochs. Per-batch readings are
          available on request for fine-grained research.
        </MKTypography>
      </Container>
    </MKBox>
  );
}
